import { useState } from 'react';
import { useAdminMessages } from '../../api/admin-queries.js';
import type { AdminMessage, AdminMessagesSummary } from '../../api/types.js';
import { formatDateTime } from '../../lib/format.js';
import { Badge } from '../badge.js';
import { EmptyState } from '../empty-state.js';
import { ErrorState } from '../error-state.js';

/** The platform takes an answer up to 30 min after the message was written. */
const ANSWER_WINDOW_SECONDS = 30 * 60;
const SECONDS_PER_MINUTE = 60;

type Filter = 'all' | 'orders' | 'doubt';

const FILTERS: readonly { value: Filter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'orders', label: 'Registrados como pedido' },
  { value: 'doubt', label: 'Descartados por duda' },
];

/** Checks that only fail when the text was readable but model, parser or clock disagreed. */
const DOUBT_CHECKS = new Set([
  'confidence',
  'kit_matches_text',
  'quantity_matches_text',
  'date_matches_text',
  'llm_available',
  'platform_accepts',
]);

const STATUS_LABELS: Readonly<Record<string, string>> = {
  PENDING: 'Pendiente',
  RESPONDED: 'Respondido',
  EXPIRED: 'Sin respuesta',
};

function formatSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  return seconds < SECONDS_PER_MINUTE
    ? `${seconds.toFixed(0)} s`
    : `${(seconds / SECONDS_PER_MINUTE).toFixed(1)} min`;
}

function isDoubtfulDiscard(message: AdminMessage): boolean {
  return (
    message.response === 'discarded' &&
    message.failedChecks.some((check) => DOUBT_CHECKS.has(check))
  );
}

function matches(message: AdminMessage, filter: Filter): boolean {
  if (filter === 'orders') return message.response === 'order';
  if (filter === 'doubt') return isDoubtfulDiscard(message);
  return true;
}

function SummaryTile({ summary }: { summary: AdminMessagesSummary }) {
  const slow =
    summary.maxAnswerSeconds !== null && summary.maxAnswerSeconds > ANSWER_WINDOW_SECONDS;
  return (
    <div className="flex flex-col gap-2 rounded-card border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">Últimas 36 h</h3>
        {summary.pending + summary.expired > 0 ? (
          <Badge tone="warning">
            {summary.pending} pendientes · {summary.expired} sin respuesta
          </Badge>
        ) : (
          <Badge tone="success">Todos respondidos</Badge>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        {[
          ['Recibidos', String(summary.received)],
          ['Pedidos', String(summary.orders)],
          ['Descartados', String(summary.discarded)],
          ['Tokens del modelo', summary.llmTokens.toLocaleString('es-CL')],
          ['Respuesta (mediana)', formatSeconds(summary.medianAnswerSeconds)],
          ['Respuesta (máxima)', formatSeconds(summary.maxAnswerSeconds)],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd
              className={`text-lg font-semibold tabular-nums ${label === 'Respuesta (máxima)' && slow ? 'text-destructive' : ''}`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">
        Plazo de respuesta: 30 min desde que el cliente escribió. Excluye los mensajes de prueba.
      </p>
    </div>
  );
}

function Answer({ message }: { message: AdminMessage }) {
  if (message.response === 'order')
    return (
      <>
        <Badge tone="success">Pedido</Badge>
        <span className="mt-1 block">
          {message.quantity} × {message.sku}
        </span>
        {message.deliveryDate !== null && (
          <span className="block text-muted-foreground">
            entrega {formatDateTime(message.deliveryDate)}
          </span>
        )}
        {message.orderId !== null && (
          <span className="block font-mono text-xs text-muted-foreground">{message.orderId}</span>
        )}
      </>
    );
  if (message.response === 'discarded')
    return (
      <>
        <Badge tone={isDoubtfulDiscard(message) ? 'warning' : 'neutral'}>Descartado</Badge>
        <span className="mt-1 block text-muted-foreground">{message.reason}</span>
      </>
    );
  return (
    <>
      <Badge tone="warning">{STATUS_LABELS[message.status] ?? message.status}</Badge>
      {message.lastError !== null && (
        <span className="mt-1 block text-muted-foreground">{message.lastError}</span>
      )}
    </>
  );
}

function MessageRow({ message }: { message: AdminMessage }) {
  return (
    <tr className="border-t border-border align-top">
      <td className="px-4 py-3 whitespace-nowrap">
        {formatDateTime(message.receivedAt)}
        {message.respondedAt !== null && (
          <span className="block text-muted-foreground">
            respondido {formatDateTime(message.respondedAt)}
          </span>
        )}
      </td>
      <td className="max-w-md px-4 py-3">
        <span className="font-medium">{message.senderName}</span>
        {message.isTest && (
          <span className="ml-2">
            <Badge>Prueba</Badge>
          </span>
        )}
        <p className="mt-1 whitespace-pre-line">{message.text}</p>
      </td>
      <td className="px-4 py-3">
        <Answer message={message} />
      </td>
      <td className="px-4 py-3">
        {message.failedChecks.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <ul className="flex flex-col gap-1 font-mono text-xs">
            {message.failedChecks.map((check) => (
              <li key={check}>{check}</li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-4 py-3">
        {message.llmOutput === null ? (
          <span className="text-muted-foreground">Sin modelo</span>
        ) : (
          <details>
            <summary className="min-h-11 cursor-pointer py-2 font-medium">
              {message.model ?? 'Modelo'}
              {message.llmTokens !== null && ` · ${String(message.llmTokens)} tokens`}
            </summary>
            <pre className="mt-2 max-w-xs overflow-x-auto rounded-control bg-muted p-2 text-xs whitespace-pre-wrap">
              {message.llmOutput}
            </pre>
          </details>
        )}
      </td>
    </tr>
  );
}

const COLUMNS = ['Recibido', 'Mensaje', 'Respuesta', 'Validaciones fallidas', 'Modelo'] as const;

/** Messaging channel: every message, how it was answered and why (task 9.10). */
export function MessagesPanel() {
  const messages = useAdminMessages();
  const [filter, setFilter] = useState<Filter>('all');
  if (messages.isError) return <ErrorState message="No pudimos cargar los mensajes." />;
  const data = messages.data;
  const visible = data?.messages.filter((message) => matches(message, filter)) ?? [];
  return (
    <section aria-labelledby="messages-title" className="flex flex-col gap-4">
      <div>
        <h2 id="messages-title" className="text-xl font-bold">
          Mensajería
        </h2>
        <p className="text-muted-foreground">
          Mensajes en lenguaje natural: cómo se interpretaron y qué se respondió.
        </p>
      </div>
      {data === undefined && <p aria-live="polite">Cargando mensajes…</p>}
      {data !== undefined && <SummaryTile summary={data.summary} />}
      {data !== undefined && data.messages.length > 0 && (
        <fieldset className="flex flex-wrap gap-2">
          <legend className="sr-only">Filtrar mensajes</legend>
          {FILTERS.map((option) => (
            <label
              key={option.value}
              className={`inline-flex min-h-11 cursor-pointer items-center rounded-control border px-4 font-medium has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring ${filter === option.value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}
            >
              <input
                type="radio"
                name="message-filter"
                value={option.value}
                checked={filter === option.value}
                onChange={() => {
                  setFilter(option.value);
                }}
                className="sr-only"
              />
              {option.label}
            </label>
          ))}
        </fieldset>
      )}
      {data?.messages.length === 0 && <EmptyState message="Todavía no llegan mensajes." />}
      {data !== undefined && data.messages.length > 0 && visible.length === 0 && (
        <EmptyState message="Ningún mensaje coincide con el filtro." />
      )}
      {visible.length > 0 && (
        <div className="overflow-x-auto rounded-card border border-border bg-card">
          <table className="w-full min-w-[64rem] text-left text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column} scope="col" className="px-4 py-3 font-medium">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((message) => (
                <MessageRow key={message.messageId} message={message} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
